import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import TextareaAutosize from '@mui/base/TextareaAutosize';
import { StyledInput } from 'components/StyledComponent';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import ImageUploading, {
  ImageListType,
  ImageType,
} from 'react-images-uploading';
import { Icon } from '@iconify/react';
import imageOutlineIcon from '@iconify/icons-eva/image-outline';
import useLazyQuery from 'hook/useLazyQuery';
import { API_PATH } from 'common/constants/services';
import { AxiosResponse } from 'axios';
import useMutation, { METHOD } from 'hook/useMutation';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router';
import isEqual from 'lodash/isEqual';
import useQuery from 'hook/useQuery';
import Loading from 'components/Loading';
import 'react-mde/lib/styles/css/react-mde-all.css';
import color from 'styles/color';
import {
  Check,
  CheckBox,
  CheckBoxOutlineBlank,
  CheckBoxOutlined,
} from '@mui/icons-material';
import useCoachList from 'hook/useCoachList';
import { queryClient } from 'App';
import MDEditor from '@uiw/react-md-editor';
import ReactMde from 'react-mde';
import * as Showdown from 'showdown';
import {
  ChipInput,
  ImageActions,
  ImageArea,
  ImageInput,
  ImageUploadArea,
  TextAreaWrapper,
} from './style';
import '../../../../styles/markdown.css';

interface IProps {
  challengeId: string | undefined;
  mode: 'new' | 'modify';
}
const converter = new Showdown.Converter({
  tables: true,
  simplifiedAutoLink: true,
  strikethrough: true,
  tasklists: true,
});
export function ChallengeForm({ challengeId, mode }: IProps) {
  const navigate = useNavigate();
  const [images, setImages] = useState<ImageType[]>([]);
  const [selectedTag, setSelectedTag] = useState<ITag[]>([]);
  const [isPro, setIsPro] = useState<boolean>(false);
  const [activate, setActivate] = useState<boolean>(false);
  const [originData, setOriginData] = useState<IChallenge | null>(null);
  const {
    register,
    handleSubmit,
    setValue,
    control,
    formState: { errors },
  } = useForm<IChallenge>();
  const tags = useQuery<TTags>(API_PATH.TAGS);

  const makeOriginData = (data: IChallengeById): IChallenge => {
    return {
      tags: data.tags,
      is_private: data.is_private,
      activated: data.activated,
      total_date: data.total_date,
      author: data.author,
      writer: data.writer,
      description: data.description,
      id: data.id,
      name: data.name,
      youtube_id: data.youtube_id,
      is_pro: data.is_pro,
      level: data.level,
    };
  };
  const [nickname, setNickname] = useState('');
  const [userId, setUserId] = useState<number | undefined>(undefined);
  const [description, setDescription] = useState<string | undefined>('');

  const setFormValue = useCallback(
    (data: IChallengeById) => {
      Object.entries(data).forEach(([key, value]) => {
        if (key === 'tags') {
          setSelectedTag(value);
        }
        if (key === 'description') {
          setDescription(value);
        }

        if (key === 'thumbnail_image') {
          setImages([{ dataURL: value }]);
        }
        if (key === 'is_pro') {
          setIsPro(value);
        }

        if (key === 'activated') {
          setActivate(value);
        }
        if (key === 'writer') {
          setUserId(value);
        }
        setValue(key as keyof IChallenge, value);
      });
    },
    [setSelectedTag, setValue],
  );

  const challengesById = useLazyQuery<IChallengeById>(
    `${API_PATH.CHALLENGES}${challengeId}`,
    {
      onSuccess: (res: AxiosResponse<IChallengeById, any>) => {
        const { data } = res;

        setFormValue(data);
        setOriginData(makeOriginData(data));
      },
    },
  );
  const postChallengeInfo = useMutation<IChallenge>(API_PATH.CHALLENGES, {
    method: METHOD.POST,
    onSuccess: (res) => {
      toast.success('등록이 완료되었습니다.');

      if (res) {
        challengesById.refetch();
        queryClient.invalidateQueries(API_PATH.CHALLENGES);
        navigate(`/challenges/modify/${(res as any).data.id}/conditions`);
      }
    },
  });
  const patchCallengeInfo = useMutation<IChallenge>(
    `${API_PATH.CHALLENGES}${challengeId}/`,
    {
      method: METHOD.PATCH,
      onSuccess: (res) => {
        toast.success('수정이 완료되었습니다.');

        if (res) {
          queryClient.invalidateQueries(API_PATH.CHALLENGES);
          challengesById.refetch();
          navigate(`/challenges/modify/${(res as any).data.id}/conditions`);
        }
      },
    },
  );

  const onChange = (imageList: ImageListType) => {
    setImages(imageList as never[]);
  };

  const onChangeTag = (item: ITag) => {
    if (selectedTag.some((tag) => tag.id === item.id)) {
      setSelectedTag(selectedTag.filter((v) => v.id !== item.id));
    } else {
      setSelectedTag([...selectedTag, item]);
    }
  };

  const onSubmit = (data: IChallenge) => {
    console.log({ userId });
    const params = {
      is_private: data.is_private === undefined ? false : data.is_private,
      activated: data.activated === undefined ? false : data.activated,
      tags: selectedTag,
      total_date: data.total_date,
      author: data.author,
      writer: userId || 0,
      description: description || data.description,
      id: data.id,
      name: data.name,
      youtube_id: data.youtube_id,
      is_pro: data.is_pro,
      level: data.level,
    };

    if (isEqual(originData, params)) {
      navigate(`/challenges/modify/${challengeId}/conditions`);

      return;
    }

    if (challengeId) {
      patchCallengeInfo.mutate(params);
    } else {
      postChallengeInfo.mutate(params);
    }
  };

  useEffect(() => {
    if (challengeId && !challengesById.data && mode === 'modify') {
      challengesById.refetch();
    }

    if (challengesById.data && !originData) {
      setFormValue(challengesById.data.data);
      setOriginData(makeOriginData(challengesById.data.data));
    }
  }, [originData, challengeId, challengesById, mode, setFormValue]);

  const isLoading = useMemo(() => {
    if (mode !== 'modify') {
      return false;
    }

    if (challengesById.isLoading) {
      return true;
    }

    return false;
  }, [challengesById, mode]);
  const { data } = useCoachList();

  const coaches = useMemo(
    () =>
      data?.items.map((user) => ({
        id: user.user_id,
        label: user.nickname || '',
      })) || [],
    [data?.items],
  );

  const coachName = useMemo(() => {
    return coaches.find((coach) => coach.id === userId)?.label;
  }, [userId, coaches]);

  const [selectedTab, setSelectedTab] = React.useState<'write' | 'preview'>(
    'write',
  );
  return (
    <Box sx={{ position: 'relative' }}>
      <Loading isLoading={isLoading} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack
          direction='row'
          alignItems='center'
          mb={1}
          sx={{
            height: '100%',
          }}
        >
          <Stack
            direction='column'
            sx={{
              gap: '8px 0px',
              width: '100%',
            }}
          >
            <StyledInput
              error={errors.name}
              type='text'
              placeholder='루틴명'
              {...register('name', { required: true })}
            />
            <StyledInput
              error={errors.author}
              type='text'
              placeholder='앱 코치명'
              label='앱 코치명'
              {...register('author', { required: true })}
            />
            <Autocomplete
              disablePortal
              fullWidth
              id='combo-box-demo'
              value={{ id: userId, label: coachName }}
              options={coaches || []}
              onChange={(event, newInputValue, reason) => {
                setUserId(newInputValue?.id || 0);
                setNickname(newInputValue?.label || '');
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  label='코치 닉네임'
                  onChange={(e: any) => {
                    setNickname(e.target.value);
                  }}
                />
              )}
            />
            <TextAreaWrapper>
              <div className='container'>
                <ReactMde
                  value={description}
                  onChange={setDescription}
                  selectedTab={selectedTab}
                  onTabChange={setSelectedTab}
                  generateMarkdownPreview={(markdown) =>
                    Promise.resolve(converter.makeHtml(markdown))
                  }
                />
              </div>
            </TextAreaWrapper>
          </Stack>
          <ImageInput>
            <ImageUploading
              multiple={false}
              value={images}
              onChange={onChange}
              maxNumber={1}
            >
              {({
                imageList,
                onImageUpload,
                onImageUpdate,
                onImageRemove,
                isDragging,
                dragProps,
              }) => {
                return (
                  <ImageUploadArea
                    style={{
                      borderColor: isDragging ? '#FFBC25' : '#C4CDD5',
                      borderWidth: isDragging ? 2 : 1,
                    }}
                  >
                    {imageList.length > 0 ? (
                      imageList.map((image, index) => (
                        <ImageArea>
                          <img src={image.dataURL} alt='' />
                          <ImageActions className='image-actions'>
                            <Button
                              size='small'
                              variant='contained'
                              onClick={() => onImageUpdate(index)}
                              sx={{ marginBottom: 2 }}
                            >
                              변경
                            </Button>
                            <Button
                              size='small'
                              variant='contained'
                              onClick={() => onImageRemove(index)}
                            >
                              삭제
                            </Button>
                          </ImageActions>
                        </ImageArea>
                      ))
                    ) : (
                      <Button
                        sx={{
                          boxSizing: 'border-box',
                          width: '100%',
                          height: '100%',
                          color: '#96a2ae',
                          display: 'flex',
                          flexDirection: 'column',
                          fontWeight: 'normal',
                        }}
                        onClick={onImageUpload}
                        {...dragProps}
                      >
                        <Icon
                          icon={imageOutlineIcon}
                          fontSize={40}
                          style={{ marginBottom: 16 }}
                        />
                        클릭하거나 드래그앤 드롭하세요.
                      </Button>
                    )}
                  </ImageUploadArea>
                );
              }}
            </ImageUploading>
          </ImageInput>
        </Stack>
        <Stack
          direction='column'
          mb={1}
          sx={{
            gap: '8px 0px',
            width: '100%',
          }}
        >
          <ChipInput>
            <Typography variant='subtitle2'>태그</Typography>
            <Stack
              direction='row'
              spacing={1}
              alignItems='center'
              justifyContent='flex-end'
            >
              {tags.getQuery.data?.data?.map((item) => (
                <Chip
                  key={item.id}
                  label={item.name}
                  color={
                    selectedTag.some((tag) => tag.id === item.id)
                      ? 'primary'
                      : 'default'
                  }
                  onClick={() => onChangeTag(item)}
                />
              ))}
            </Stack>
          </ChipInput>

          <StyledInput
            type='number'
            error={errors.total_date}
            {...register('total_date', { required: true })}
            placeholder='기간(일)'
          />

          <StyledInput
            type='number'
            error={errors.level}
            {...register('level', { required: true })}
            placeholder='레벨 (100 - 300)'
          />

          <StyledInput
            type='text'
            placeholder='유튜브링크'
            {...register('youtube_id')}
          />
        </Stack>
        <Stack direction='row' spacing={1} alignItems='center'>
          <Controller
            name='is_pro'
            control={control}
            rules={{ required: false }}
            render={({ field }) => {
              return (
                <Button
                  onClick={() => {
                    setIsPro(!isPro);
                    field.onChange(!isPro);
                  }}
                  role='button'
                >
                  {isPro ? <CheckBoxOutlined /> : <CheckBoxOutlineBlank />}
                </Button>
              );
            }}
          />
          <Typography variant='subtitle2'>PRO 루틴</Typography>
        </Stack>
        <Stack direction='row' spacing={1} alignItems='center'>
          <Controller
            name='activated'
            control={control}
            rules={{ required: false }}
            render={({ field }) => {
              return (
                <Button
                  onClick={() => {
                    setActivate(!activate);
                    field.onChange(!activate);
                  }}
                  role='button'
                >
                  {activate ? <CheckBoxOutlined /> : <CheckBoxOutlineBlank />}
                </Button>
              );
            }}
          />
          <Typography variant='subtitle2'>앱 공개여부</Typography>
        </Stack>

        <Button variant='contained' fullWidth type='submit'>
          저장(다음)
        </Button>
        {/* <input type='submit' /> */}
      </form>
    </Box>
  );
}
